import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addToWishlist, removeFromWishlist } from '../../actions/wishlistAction';
import { useSnackbar } from 'notistack';
import { Box, Card, CardActionArea, CardContent, CardMedia, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const Product = ({ _id, name, images, ratings = 0, numOfReviews = 0, price: rawPrice = 0, mrp: rawMrp = 0, highlights }) => {
    const price = Number(rawPrice);
    const mrp = Number(rawMrp);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const { wishlistItems } = useSelector((state) => state.wishlist);
    const itemInWishlist = wishlistItems.some((i) => i.product === _id);

    const addToWishlistHandler = () => {
        if (itemInWishlist) {
            dispatch(removeFromWishlist(_id));
            enqueueSnackbar("Removed from Wishlist", { variant: "success" });
        } else {
            dispatch(addToWishlist(_id));
            enqueueSnackbar("Added to Wishlist", { variant: "success" });
        }
    };

    const getDiscount = (price, mrp) => {
        return Math.round(((mrp - price) / mrp) * 100);
    };

    return (
        <Card sx={{
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
                transform: 'translateY(-10px)',
            },
        }}>
            <CardActionArea component={Link} to={`/product/${_id}`}>
                <CardMedia
                    component="img"
                    height="200"
                    image={images && images[0].lowUrl}
                    alt={name}
                    sx={{ objectFit: 'contain', p: 1 }}
                />
                <CardContent>
                    <Typography
                        gutterBottom
                        variant="body2"
                        component="div"
                        noWrap
                        title={name}
                    >
                        {name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon sx={{ fontSize: '1rem', mr: 0.5, color: '#FFC107' }} /> {ratings.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({numOfReviews} reviews)
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" component="div" sx={{ mr: 1 }}>
                            ₹{price.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', mr: 1 }}>
                            {mrp > price && `M.R.P.: ₹${mrp.toLocaleString()}`}
                        </Typography>
                        {mrp > price && (
                            <Typography variant="body2" color="success.main">
                                {getDiscount(price, mrp)}% off
                            </Typography>
                        )}
                    </Box>
                    {highlights && highlights.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {highlights.map((highlight, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                        color: theme.palette.text.primary,
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        display: 'inline-block',
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                                    }}
                                >
                                    <Typography variant="body2">
                                        {highlight}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </CardActionArea>
            <Tooltip title={itemInWishlist ? "Remove from Wishlist" : "Add to Wishlist"}>
                <FavoriteIcon
                    onClick={addToWishlistHandler}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        cursor: 'pointer',
                        color: itemInWishlist ? theme.palette.error.main : theme.palette.grey[400],
                        '&:hover': {
                            color: itemInWishlist ? theme.palette.error.dark : theme.palette.error.light,
                        },
                    }}
                />
            </Tooltip>
        </Card>
    );
};

export default Product;